/**
 * One Idempotency-Key per user intent (async reliability lane, ASYNC-6).
 *
 * The backend returns the EXISTING job for a key the user already used, even
 * when that job failed. So:
 * - a double-click / double-submit of the same intent must send the same key
 *   (the second request replays the first job instead of charging twice);
 * - a user-initiated "Try again" after a failure must send a NEW key.
 *
 * Kept free of axios/expo imports so it stays unit-testable.
 */
import { useRef } from 'react';

export function createIdempotencyKey(): string {
  const cryptoApi = (globalThis as any)?.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Key for a follow-up job that belongs to the same intent (e.g. the analysis
 * chained after a transcription upload). Must differ from the parent key: the
 * backend keys are unique per user across job kinds.
 */
export function derivedIdempotencyKey(parentKey: string, step: string): string {
  return `${parentKey}:${step}`;
}

/**
 * Idempotency headers for `POST /api/ai/message`. An analysis request always
 * carries a key (minted when the caller has none). Any other request carries
 * the caller's key when given: the server classifies a chat message, and a
 * target-level re-assessment or same-transcript re-analysis it turns into is
 * a billed job that the key deduplicates.
 */
export function messageIdempotencyHeaders(
  options: { isAnalysisRequest: boolean; idempotencyKey?: string },
  mint: () => string = createIdempotencyKey,
): Record<string, string> {
  if (options.idempotencyKey) return { 'Idempotency-Key': options.idempotencyKey };
  return options.isAnalysisRequest ? { 'Idempotency-Key': mint() } : {};
}

/**
 * Intent input for a typed chat message: the same text re-sent to the same
 * session (the draft restored after a failed send) is the same intent.
 */
export function chatSendIntentInput(sessionId: number | null | undefined, message: string): string {
  return `${sessionId ?? 'new'}\u0000${message}`;
}

export interface IdempotencyIntent {
  /**
   * Key of the current intent; minted on first use, stable until reset.
   * Pass the intent's input (the picked file object, a document id, ...):
   * the same input keeps the key (double submit), a different input begins
   * a new intent and mints a new key.
   */
  key(input?: unknown): string;
  /** Forget the key: the job reached a terminal state or the intent ended. */
  reset(): void;
  /** Start a fresh intent (user-initiated "Try again") and return its new key. */
  renew(input?: unknown): string;
}

export function createIdempotencyIntent(mint: () => string = createIdempotencyKey): IdempotencyIntent {
  let current: string | null = null;
  let currentInput: unknown;
  return {
    key(input?: unknown) {
      if (!current || (input !== undefined && !Object.is(input, currentInput))) {
        current = mint();
        currentInput = input;
      }
      return current;
    },
    reset() {
      current = null;
      currentInput = undefined;
    },
    renew(input?: unknown) {
      current = mint();
      if (input !== undefined) currentInput = input;
      return current;
    },
  };
}

/** One intent per component instance (survives re-renders). */
export function useIdempotencyIntent(): IdempotencyIntent {
  const ref = useRef<IdempotencyIntent | null>(null);
  if (!ref.current) ref.current = createIdempotencyIntent();
  return ref.current;
}
